import React, { useEffect, useRef, useState } from "react";

interface UseActionPaletteKeyboardParams {
  itemCount: number;
  isOpen: boolean;
  // Changing this resets the highlight (e.g. the selected row).
  resetKey: unknown;
  onEnter: (index: number) => void;
  onClose: () => void;
}

interface UseActionPaletteKeyboardReturn {
  highlightedIndex: number;
  setHighlightedIndex: React.Dispatch<React.SetStateAction<number>>;
  itemRefs: React.MutableRefObject<(HTMLButtonElement | null)[]>;
  handleKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => void;
}

// Owns keyboard navigation for the palette: tracks the highlighted row, wraps
// Arrow Up/Down, runs the highlighted action on Enter, closes on Escape, keeps
// the highlight in range, and scrolls it into view.
export function useActionPaletteKeyboard({
  itemCount,
  isOpen,
  resetKey,
  onEnter,
  onClose,
}: UseActionPaletteKeyboardParams): UseActionPaletteKeyboardReturn {
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // Start at the top whenever the palette opens or the row changes.
  useEffect(() => {
    setHighlightedIndex(0);
  }, [isOpen, resetKey]);

  // Keep the highlight in range as the filtered list grows/shrinks.
  useEffect(() => {
    setHighlightedIndex((prev) =>
      itemCount === 0 ? 0 : Math.min(prev, itemCount - 1)
    );
  }, [itemCount]);

  // Scroll the highlighted row into view.
  useEffect(() => {
    itemRefs.current[highlightedIndex]?.scrollIntoView({ block: "nearest" });
  }, [highlightedIndex]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((prev) =>
        itemCount === 0 ? 0 : (prev + 1) % itemCount
      );
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((prev) =>
        itemCount === 0 ? 0 : (prev - 1 + itemCount) % itemCount
      );
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      onEnter(highlightedIndex);
      return;
    }
    if (e.key === "Escape") {
      e.preventDefault();
      onClose();
    }
  };

  return { highlightedIndex, setHighlightedIndex, itemRefs, handleKeyDown };
}
